import { useState, useEffect } from "react";
import AccountLayout from "@/components/feature/AccountLayout";
import { mockOrders, statusLabelsEn, statusLabelsAr } from "@/mocks/orders";
import { allProducts } from "@/mocks/products";
import { useCart } from "@/hooks/useCart";
import type { Order } from "@/mocks/orders";

export default function OrderDetailPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const { id } = useParams();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [reorderSuccess, setReorderSuccess] = useState<{ added: number; skipped: number } | null>(null);
  const { addItem } = useCart();

  useEffect(() => {
    // Check localStorage orders first, then mock
    let found: Order | null = null;

    try {
      const raw = localStorage.getItem("generalstore_orders");
      if (raw) {
        const localOrders = JSON.parse(raw);
        if (Array.isArray(localOrders)) {
          found = localOrders.find((o: Order) => o.id === id) || null;
        }
      }
    } catch {
      // corrupted
    }

    if (!found) {
      found = mockOrders.find((o) => o.id === id) || null;
    }

    setOrder(found);
    setLoading(false);
  }, [id]);

  if (loading) {
    return (
      <AccountLayout activeTab="orders">
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div>
        </div>
      </AccountLayout>
    );
  }

  if (!order) {
    return (
      <AccountLayout activeTab="orders">
        <div className="text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
          </div>
          <h3 className="text-lg font-medium text-foreground-900 mb-2">Order not found</h3>
          <p className="text-sm text-foreground-500 mb-6">The order you are looking for does not exist.</p>
          <Link
            to="/account/orders"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            {t("account.back_to_orders")}
          </Link>
        </div>
      </AccountLayout>
    );
  }

  const getStatusLabel = (status: string) =>
    isAr ? statusLabelsAr[status as keyof typeof statusLabelsAr] : statusLabelsEn[status as keyof typeof statusLabelsEn];

  const statusStyles: Record<string, string> = {
    delivered: "bg-secondary-100 text-secondary-700",
    shipped: "bg-accent-100 text-accent-700",
    processing: "bg-primary-100 text-primary-700",
    cancelled: "bg-foreground-100 text-foreground-500",
  };

  const handleReorder = () => {
    if (!order) return;
    let added = 0;
    let skipped = 0;

    order.items.forEach((item) => {
      // Check if product exists in mock data
      const product = allProducts.find((p) => p.id === item.productId);
      if (!product || !product.inStock) {
        skipped++;
        return;
      }
      addItem({
        productId: item.productId,
        nameEn: item.nameEn,
        nameAr: item.nameAr,
        slug: product.slug,
        price: item.price,
        imageUrl: item.imageUrl,
        quantity: item.quantity,
        maxStock: product.stockCount,
        selectedVariants: item.selectedVariants,
      });
      added++;
    });

    setReorderSuccess({ added, skipped });
    setTimeout(() => setReorderSuccess(null), 4000);
  };

  return (
    <AccountLayout activeTab="orders">
      {/* Back link */}
      <Link
        to="/account/orders"
        className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-900 transition-colors cursor-pointer mb-6"
      >
        <i className={isAr ? "ri-arrow-right-line" : "ri-arrow-left-line"}></i>
        {t("account.back_to_orders")}
      </Link>

      {/* Order header */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-xl font-heading font-bold text-foreground-950">
                {t("account.order_number")} #{order.orderNumber}
              </h2>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusStyles[order.status]}`}
              >
                {getStatusLabel(order.status)}
              </span>
            </div>
            <p className="text-sm text-foreground-500">
              {t("account.order_placed")}{" "}
              {new Date(order.date).toLocaleDateString(
                isAr ? "ar-EG" : "en-US",
                { year: "numeric", month: "long", day: "numeric" }
              )}
            </p>
          </div>
          {order.trackingNumber && (
            <div className="text-right">
              <p className="text-xs text-foreground-500 mb-1">{t("account.tracking_number")}</p>
              <p className="text-sm font-mono font-medium text-foreground-800">{order.trackingNumber}</p>
              {order.estimatedDelivery && (
                <p className="text-xs text-foreground-400 mt-0.5">
                  {t("account.estimated_delivery")}:{" "}
                  {new Date(order.estimatedDelivery).toLocaleDateString(
                    isAr ? "ar-EG" : "en-US",
                    { month: "short", day: "numeric" }
                  )}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className={`grid grid-cols-1 lg:grid-cols-3 gap-6 ${isAr ? "lg:flex-row-reverse" : ""}`}>
        {/* Main — items + shipping + payment */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order items */}
          <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
            <div className="p-5 border-b border-background-100">
              <h3 className="text-sm font-semibold text-foreground-900">
                {order.items.length} {order.items.length === 1 ? t("account.item") : t("account.items")}
              </h3>
            </div>
            <div className="divide-y divide-background-100">
              {order.items.map((item, i) => (
                <div key={i} className="p-5 flex items-center gap-4">
                  <div className="w-16 h-16 rounded-lg bg-background-100 overflow-hidden flex-shrink-0">
                    <img
                      src={item.imageUrl}
                      alt={isAr ? item.nameAr : item.nameEn}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/product/${item.productId}`}
                      className="text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors line-clamp-1 cursor-pointer"
                    >
                      {isAr ? item.nameAr : item.nameEn}
                    </Link>
                    {Object.keys(item.selectedVariants).length > 0 && (
                      <p className="text-xs text-foreground-500 mt-0.5">
                        {Object.entries(item.selectedVariants)
                          .map(([k, v]) => v)
                          .join(" / ")}
                      </p>
                    )}
                    <p className="text-xs text-foreground-400 mt-0.5">
                      {t("cart.quantity")}: {item.quantity}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">
                    ${(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Shipping address */}
          <div className="bg-background-50 border border-background-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
              <i className="ri-map-pin-line text-foreground-500"></i>
              {t("account.shipping_address")}
            </h3>
            <div className="text-sm text-foreground-700 space-y-0.5">
              <p className="font-medium">{order.shippingAddress.fullName}</p>
              <p>{order.shippingAddress.street}</p>
              <p>
                {order.shippingAddress.city}, {order.shippingAddress.state}{" "}
                {order.shippingAddress.zip}
              </p>
              <p>{order.shippingAddress.country}</p>
              <p className="text-foreground-500 mt-2">{order.shippingAddress.phone}</p>
            </div>
          </div>
        </div>

        {/* Sidebar — order summary */}
        <div className="space-y-6">
          {/* Summary */}
          <div className="bg-background-50 border border-background-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-foreground-900 mb-4">
              {t("account.order_summary")}
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-foreground-500">{t("account.subtotal")}</span>
                <span className="text-foreground-800">${order.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-foreground-500">{t("account.shipping")}</span>
                <span className="text-foreground-800">
                  {order.shipping === 0 ? t("cart.free_shipping") : `$${order.shipping.toFixed(2)}`}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-foreground-500">{t("account.tax")}</span>
                <span className="text-foreground-800">${order.tax.toFixed(2)}</span>
              </div>
              <div className="border-t border-background-200 pt-3 flex justify-between">
                <span className="font-semibold text-foreground-900">{t("cart.total")}</span>
                <span className="font-bold text-foreground-950">${order.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Payment */}
          <div className="bg-background-50 border border-background-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
              <i className="ri-bank-card-line text-foreground-500"></i>
              {t("account.payment_method")}
            </h3>
            <div className="flex items-center gap-3">
              <div className="w-10 h-7 rounded bg-background-200 flex items-center justify-center">
                <span className="text-xs font-bold text-foreground-500">
                  {order.paymentMethod.type === "Visa" ? "V" : "MC"}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground-800">{order.paymentMethod.type}</p>
                <p className="text-xs text-foreground-500">•••• {order.paymentMethod.last4}</p>
              </div>
            </div>
          </div>

          {/* Action */}
          {reorderSuccess ? (
            <div className="bg-secondary-50 border border-secondary-200 rounded-xl p-4 flex items-center gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="w-8 h-8 rounded-full bg-secondary-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-check-line text-white text-sm"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-secondary-800">
                  {reorderSuccess.skipped === 0
                    ? t("account.reorder_success")
                    : t("account.reorder_partial", { count: reorderSuccess.added, skipped: reorderSuccess.skipped })}
                </p>
                <Link
                  to="/cart"
                  className="text-xs text-secondary-600 hover:text-secondary-800 underline cursor-pointer mt-0.5 inline-block"
                >
                  {t("nav.cart")} &rarr;
                </Link>
              </div>
            </div>
          ) : (
            <button
              onClick={handleReorder}
              disabled={order.status === "cancelled"}
              className="w-full py-3 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer text-sm whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-refresh-line"></i>
              {t("account.reorder")}
            </button>
          )}
        </div>
      </div>
    </AccountLayout>
  );
}